import React, {useEffect, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import styles from './UploadInfoModal.module.css';
import { PRISMAX_DATA_PIPELINE_URL } from '../../configs/config';

function getTaskTitle(task) {
    if (task?.scenario) return task.scenario;
    if (task?.details_description) return task.details_description;
    return `Task ${task?.task_id ?? '-'}`;
}

function getTaskDescription(task) {
    if (task?.details_description) return task.details_description;
    if (task?.notes) return task.notes;
    return 'No description provided.';
}

function getObjectFilename(objectName, fallbackText = '') {
    const full = String(objectName || '').trim();
    if (!full) return fallbackText;
    return full.split('/').pop() || fallbackText;
}

function getStableHash(text) {
    let hash = 0;
    const value = String(text || '');
    for (let index = 0; index < value.length; index += 1) {
        hash = ((hash * 31) + value.charCodeAt(index)) >>> 0;
    }
    return hash;
}

function parseListField(value) {
    const text = String(value || '').trim();
    if (!text) return [];
    return text
        .split('\n')
        .flatMap((line) => {
            const trimmed = line.trim();
            if (!trimmed) return [];
            return [trimmed.replace(/^-\s*/, '')];
        })
        .filter(Boolean);
}

function parseTagField(value) {
    const text = String(value || '').trim();
    if (!text) return [];
    return text
        .split(/,(?![^(]*\))/g) // Do not split commas inside paranthesis e.g. a (a1, a2, a3) is considered one tag
        .map((s) => s.trim())
        .map((s) => `${s.charAt(0).toUpperCase()}${s.substring(1)}`)
        .filter(Boolean);
}

function sortVideos(videos, episodeId) {
    const decorated = videos.map((video, index) => {
        const filename = getObjectFilename(video?.object_name, '').toLowerCase();
        const hasLeft = filename.includes('left');
        const hasRight = filename.includes('right');
        return {
            video,
            index,
            hasLeft,
            hasRight,
            randomRank: getStableHash(`${episodeId || 'episode'}:${filename}:${index}`),
        };
    });

    decorated.sort((a, b) => {
        // Environment view (neither left nor right) first, then left, then right.
        const rankA = a.hasLeft ? 1 : a.hasRight ? 2 : 0;
        const rankB = b.hasLeft ? 1 : b.hasRight ? 2 : 0;
        if (rankA !== rankB) return rankA - rankB;
        return a.randomRank - b.randomRank;
    });

    return decorated.map((d) => d.video);
}

function UploadInfoModal({task, onClose, onUpload}) {
    const root = document.getElementById('root');
    const [videos, setVideos] = useState([]);
    const [videosLoading, setVideosLoading] = useState(true);
    const videoRefs = useRef([]);
    const thumbScrollRef = useRef(null);
    const moreHintRef = useRef(null);

    useEffect(() => {
        if (!task?.task_id) return;
        setVideosLoading(true);
        setVideos([]);
        videoRefs.current = [];

        fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/tasks/uploadable-task-preview/${task.task_id}`)
            .then((res) => res.json())
            .then((data) => {
                if (data?.success && Array.isArray(data.data?.videos)) {
                    const sorted = sortVideos(data.data.videos, data.data.episode_id);
                    setVideos(sorted);
                }
            })
            .catch(() => {})
            .finally(() => setVideosLoading(false));
    }, [task?.task_id]);

    // Play all videos in sync once all have loaded metadata
    useEffect(() => {
        const refs = videoRefs.current.filter(Boolean);
        if (refs.length === 0 || refs.length !== videos.length) return;

        let loadedCount = 0;

        function tryPlayAll() {
            loadedCount += 1;
            if (loadedCount < refs.length) return;
            refs.forEach((v) => {
                v.currentTime = 0;
                v.play().catch(() => {});
            });
        }

        refs.forEach((v) => {
            v.pause();
            v.currentTime = 0;
            if (v.readyState >= 1) {
                tryPlayAll();
            } else {
                v.addEventListener('loadedmetadata', tryPlayAll, {once: true});
            }
        });

        return () => {
            refs.forEach((v) => {
                v.removeEventListener('loadedmetadata', tryPlayAll);
                v.pause();
            });
        };
    }, [videos]);

    // Show/hide the more-hint based on scroll position
    useEffect(() => {
        const thumbScroll = thumbScrollRef.current;
        const moreHint = moreHintRef.current;
        if (!thumbScroll || !moreHint) return;

        function updateHint() {
            const atBottom =
                thumbScroll.scrollTop + thumbScroll.clientHeight >= thumbScroll.scrollHeight - 4;
            moreHint.style.display = atBottom ? 'none' : 'flex';
        }

        thumbScroll.addEventListener('scroll', updateHint);
        updateHint();

        return () => thumbScroll.removeEventListener('scroll', updateHint);
    }, [videos]);

    const objectiveTags = parseTagField(task?.objects);
    const actionTags = parseTagField(task?.action);
    const taskVariations = parseListField(task?.task_variations);
    const generalVariations = parseListField(task?.general_variations);
    const noteText = task?.notes ? String(task.notes).trim() : '';

    if (!task || !root) return null;

    return ReactDOM.createPortal(
        <div
            className={styles.modalOverlay}
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose?.();
            }}
        >
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <div className={styles.modalTitle}>{getTaskTitle(task)}</div>
                    <button type="button" className={styles.closeBtn} onClick={onClose}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path
                                d="M1 1l8 8M9 1L1 9"
                                stroke="rgba(255,255,255,0.6)"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                            />
                        </svg>
                    </button>
                </div>

                <p className={styles.modalDesc}>{getTaskDescription(task)}</p>

                <div className={styles.modalTabs}>
                    <div className={styles.tab}>Examples</div>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.mediaCol}>
                        <div className={styles.thumbScroll} ref={thumbScrollRef}>
                            {videosLoading && (
                                <>
                                    <div className={styles.thumbSkeleton} />
                                    <div className={styles.thumbSkeleton} />
                                </>
                            )}
                            {!videosLoading && videos.map((video, i) => {
                                const label = getObjectFilename(video.object_name, `Video ${i + 1}`);
                                return (
                                    <div key={video.object_name || i} className={styles.thumb}>
                                        <video
                                            ref={(el) => { videoRefs.current[i] = el; }}
                                            src={video.url}
                                            className={styles.thumbVideo}
                                            muted
                                            loop
                                            playsInline
                                            preload="auto"
                                            disablePictureInPicture 
                                        />
                                        <span className={styles.thumbCam}>{label}</span>
                                    </div>
                                );
                            })}
                            {!videosLoading && videos.length === 0 && (
                                <div className={styles.thumbEmpty}>No preview available</div>
                            )}
                        </div>

                        <div
                            className={styles.moreHint}
                            ref={moreHintRef}
                            onClick={() => {
                                thumbScrollRef.current?.scrollBy({top: 200, behavior: 'smooth'});
                            }}
                        >
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                <path
                                    d="M5 2v6M2 6l3 3 3-3"
                                    stroke="rgba(255,255,255,0.6)"
                                    strokeWidth="1.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <span>1 more video</span>
                        </div>
                    </div>

                    <div className={styles.infoCol}>
                        {objectiveTags.length > 0 && (
                            <div className={styles.infoSection}>
                                <div className={styles.sectionLabel}>Objectives</div>
                                <div className={styles.tagRow}>
                                    {objectiveTags.map((tag) => (
                                        <span key={tag} className={`${styles.tag} ${styles.tagHighlight}`}>
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className={styles.divider} />

                        {actionTags.length > 0 && (
                            <div className={styles.infoSection}>
                                <div className={styles.sectionLabel}>Action type</div>
                                <div className={styles.tagRow}>
                                    {actionTags.map((tag) => (
                                        <span key={tag} className={styles.tag}>{tag}</span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className={styles.divider} />

                        <div className={styles.twoCol}>
                            {taskVariations.length > 0 && (
                                <div className={styles.infoSection}>
                                    <div className={styles.sectionLabel}>Task variations</div>
                                    <div className={styles.variationList}>
                                        {taskVariations.map((item) => (
                                            <div key={item} className={styles.variationItem}>{item}</div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {generalVariations.length > 0 && (
                                <div className={styles.infoSection}>
                                    <div className={styles.sectionLabel}>General variations</div>
                                    <div className={styles.variationList}>
                                        {generalVariations.map((item) => (
                                            <div key={item} className={styles.variationItem}>{item}</div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {noteText && (
                            <div className={styles.noteBox}>
                                <div className={styles.noteIcon}>
                                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                                        <circle cx="6.5" cy="6.5" r="5.5" stroke="rgba(240,160,75,0.7)" strokeWidth="1" />
                                        <path d="M6.5 5.5v3" stroke="rgba(240,160,75,0.7)" strokeWidth="1.2" strokeLinecap="round" />
                                        <circle cx="6.5" cy="4" r="0.7" fill="rgba(240,160,75,0.7)" />
                                    </svg>
                                </div>
                                <span>{noteText}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <span className={styles.footerHint}>Upload data now to earn PrismaX points.</span>
                    <div className={styles.footerRight}>
                        {/* <button type="button" className={styles.btnSecondary}>
                            View guidelines
                        </button> */}
                        <button type="button" className={styles.btnPrimary} onClick={onUpload}>
                            <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                                <path
                                    d="M5.5 8V1M2.5 4L5.5 1L8.5 4"
                                    stroke="#111"
                                    strokeWidth="1.3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M1.5 8.5V9.5C1.5 10.0523 1.94772 10.5 2.5 10.5H8.5C9.05228 10.5 9.5 10.0523 9.5 9.5V8.5"
                                    stroke="#111"
                                    strokeWidth="1.3"
                                    strokeLinecap="round"
                                />
                            </svg>
                            Upload data
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        root
    );
}

export default UploadInfoModal;
